/**
 * Pre-decodificación: decodifica imágenes por adelantado (fuera de pantalla) para que, cuando
 * se pintan, el WebView ya tenga el bitmap en su caché de decodificación. Secuencial a propósito:
 * en 4 núcleos A55 dos decodificaciones de 2160 px en paralelo compiten con la UI.
 *
 * img.decode() puede no resolver nunca si el WebView deja de pintar (pantalla apagada,
 * ventana oculta): todo lleva timeout para que nada quede esperando.
 */
const queue: string[] = [];
const doneSet = new Set<string>();
let running = false;

export type DecodeResult = 'ok' | 'error' | 'timeout';

export function predecode(urls: Array<string | null | undefined>, front = false) {
  const fresh = urls.filter((u): u is string => !!u && !doneSet.has(u) && !queue.includes(u));
  if (front) queue.unshift(...fresh);
  else queue.push(...fresh);
  if (queue.length > 60) queue.length = 60;
  if (!running) void pump();
}

async function pump() {
  running = true;
  while (queue.length) {
    await decodeOne(queue.shift() as string);
    // cede el hilo principal entre decodificaciones
    await new Promise((r) => setTimeout(r, 16));
  }
  running = false;
}

/** Decodifica una imagen; 'timeout' si el WebView no respondió en `ms` */
export function decodeOne(url: string, ms = 2500): Promise<DecodeResult> {
  if (!url) return Promise.resolve('error');
  if (doneSet.has(url)) return Promise.resolve('ok');
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  return Promise.race<DecodeResult>([
    img.decode().then(
      () => {
        doneSet.add(url);
        if (doneSet.size > 400) doneSet.clear();
        return 'ok';
      },
      () => 'error',
    ),
    new Promise<DecodeResult>((r) => setTimeout(() => r('timeout'), ms)),
  ]);
}
